import { Drawer } from 'antd';
import { LoadingSpinner } from 'core/components/header/components/report/drawers/components/loading-spinner.component';
import { MEDIA_QUERIES } from 'config/style/breakpoints';
import { lazyRoute } from 'infra/routes/lazy-route';
import { useMediaQuery } from 'beautiful-react-hooks';
import { useEffect, useRef, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import styles from './reports-overlay-drawer.component.module.css';
import useReportsOverlayStore from './reports-overlay.store';

const ReportsListContainer = lazyRoute(() => import('pages/reports-list/reports-list.container'));

/**
 * Renders the new-design Reports catalog and export drawers as an overlay on top of whatever page
 * the reader is on — mounted once, in the topbar, so it survives every route instead of navigating
 * away and hiding the timeline underneath it (P40-64021).
 */
export function ReportsOverlayDrawer() {
  const { isOpen, propertyId, companyId, close } = useReportsOverlayStore(
    useShallow(state => ({ isOpen: state.isOpen, propertyId: state.propertyId, companyId: state.companyId, close: state.close }))
  );
  /*
   * The reports page's own back button is the only close affordance on a wide screen — but it hides
   * below this same breakpoint (reports-landing.component.tsx), on the assumption that a native
   * WebView header already carries that control there. That assumption doesn't hold for the overlay
   * itself: a *browser* window can cross this width too (a resized desktop window, an iPad in
   * portrait) with no such chrome, so the drawer's own close paths come back at that width instead
   * of leaving the reader stuck behind a fully locked-down Drawer.
   */
  const isMobile = useMediaQuery(MEDIA_QUERIES.MOBILE);

  /*
   * The drawer sits on top of whatever page it opened over instead of being reached by navigation,
   * so nothing about routing closes it on its own — browser back changes the page underneath while
   * the drawer stays open on top, still holding the propertyId/companyId it opened with.
   */
  const { pathname } = useLocation();
  const previousPathname = useRef(pathname);

  useEffect(() => {
    if (pathname !== previousPathname.current) {
      previousPathname.current = pathname;
      close();
    }
  }, [pathname, close]);

  return (
    <Drawer
      open={isOpen}
      onClose={close}
      closable={isMobile}
      maskClosable={isMobile}
      keyboard={isMobile}
      /* Without this, antd's nested-drawer convention shifts this drawer sideways whenever a report
       * card opens its own drawer on top of it — there's nothing behind this one for that motion to
       * meaningfully reveal, so it reads as an unwanted jump instead of a hint. */
      push={false}
      /* `width` is deprecated in favor of `size`, which only takes a preset or a raw number — no
       * room for a responsive `min()` expression, so the value moves onto the wrapper's own style. */
      styles={{ wrapper: { width: 'min(600px, 100vw)' }, body: { padding: 0 } }}
      placement='right'>
      {isOpen && (
        <div className={styles.host}>
          <Suspense fallback={<LoadingSpinner />}>
            <ReportsListContainer propertyId={propertyId} companyId={companyId} onClose={close} />
          </Suspense>
        </div>
      )}
    </Drawer>
  );
}
